import * as PL2 from '@common/utils/dist/models/models-pl2.js';

import {GraphConfigUtils as GCU} from '@utils/graph-config-utils';
import {UnitUtils as UU} from '@utils/unit-utils';
import {ChartUtils as CU} from '@utils/chart-utils';
import {DataConfigStateUtils as DCSU} from '@utils/data-config-state-utils';

import {
  TrialDataWindow,
  GraphSummary,
  ChannelSummary,
  TrendType,
} from '@models/trial-data-window-types';

const MIN_POINTS_FOR_TREND = 5;

interface BuildTrialDataWindowParams {
  dataConfigState: PL2.DataConfigState;
  dataZoomState: {minMaxIndex: Record<string, number[]>};
  trialPK: string;
  trialName: string;
  cardPK: string;
  deviceName: string;
}

/** A saved trial's plots as recorded data, the way the trial's own data view loads them. */
export function recordedDataConfigState(
  plots: PL2.Plot[],
): PL2.DataConfigState {
  const state: PL2.DataConfigState = {};
  for (const plot of plots) {
    state[plot.cfgId] = {
      l: plot.l,
      xL: plot.xL,
      yL: plot.yL,
      p: plot.p,
      sD: plot.sD,
      loc: plot.loc,
      f: plot.f,
      uIdxs: plot.uIdxs,
      dS: PL2.DataStatus.Recorded,
    };
  }
  return state;
}

export function buildTrialDataWindow(
  params: BuildTrialDataWindowParams,
): TrialDataWindow | null {
  const {
    dataConfigState,
    dataZoomState,
    trialPK,
    trialName,
    cardPK,
    deviceName,
  } = params;

  const graphConfigs = DCSU.sensorGraphConfigs(dataConfigState);
  if (!graphConfigs.length) return null;

  // Use the first config with data for top-level timing info
  const primaryGC = graphConfigs.find(
    (gc) => dataConfigState[gc.id]?.p?.length,
  );
  if (!primaryGC) return null;

  const primaryDvc = dataConfigState[primaryGC.id];
  const status = mapDataStatus(primaryDvc.dS);
  const sampleRate = primaryDvc.f ?? 1;

  const firstPoint = primaryDvc.p[0];
  const lastPoint = primaryDvc.p[primaryDvc.p.length - 1];
  const firstTime = CU.timeFromPoint(firstPoint) ?? 0;
  const lastTime = CU.timeFromPoint(lastPoint) ?? 0;
  const elapsedSeconds = lastTime - firstTime;

  // Build summaries grouped by graph config
  const allChannels: string[] = [];
  const trialGraphSummaries: GraphSummary[] = [];
  const windowGraphSummaries: GraphSummary[] = [];
  let totalSamples = 0;

  for (const gC of graphConfigs) {
    const dvc = dataConfigState[gC.id];
    if (!dvc?.p?.length) continue;

    const channels = dvc.l ?? gC.labels ?? [];
    allChannels.push(...channels);
    totalSamples = Math.max(totalSamples, dvc.p.length);

    const gcFirstTime = CU.timeFromPoint(dvc.p[0]) ?? 0;
    const gcLastTime = CU.timeFromPoint(dvc.p[dvc.p.length - 1]) ?? 0;
    const gcElapsed = gcLastTime - gcFirstTime;

    trialGraphSummaries.push({
      graphName: gC.commonName ?? gC.name ?? gC.id,
      channels: buildChannelSummaries(dvc.p, gC, dvc, gcElapsed),
    });

    const zoomRange = dataZoomState.minMaxIndex[gC.id];
    let windowPoints = dvc.p;
    let windowElapsed = gcElapsed;

    if (zoomRange?.length === 2) {
      const [startIdx, endIdx] = zoomRange;
      windowPoints = dvc.p.slice(startIdx, endIdx + 1);
      if (windowPoints.length) {
        const wStart = CU.timeFromPoint(windowPoints[0]) ?? gcFirstTime;
        const wEnd =
          CU.timeFromPoint(windowPoints[windowPoints.length - 1]) ?? gcLastTime;
        windowElapsed = wEnd - wStart;
      }
    }

    windowGraphSummaries.push({
      graphName: gC.commonName ?? gC.name ?? gC.id,
      channels: buildChannelSummaries(windowPoints, gC, dvc, windowElapsed),
    });
  }

  // Window bounds from primary config
  const zoomRange = dataZoomState.minMaxIndex[primaryGC.id];
  let windowStartSeconds = firstTime;
  let windowEndSeconds = lastTime;
  let windowSamples = primaryDvc.p.length;

  if (zoomRange?.length === 2) {
    const [startIdx, endIdx] = zoomRange;
    const windowPoints = primaryDvc.p.slice(startIdx, endIdx + 1);
    windowSamples = windowPoints.length;
    if (windowPoints.length) {
      windowStartSeconds = CU.timeFromPoint(windowPoints[0]) ?? firstTime;
      windowEndSeconds =
        CU.timeFromPoint(windowPoints[windowPoints.length - 1]) ?? lastTime;
    }
  }

  return {
    type: 'DATA_WINDOW',
    trialPK,
    trialName,
    cardPK,
    deviceName,
    state: {
      status,
      sampleRate,
      channels: allChannels,
      startedAt: primaryDvc.sD ?? undefined,
    },
    trialSummary: trialGraphSummaries,
    windowSummary: windowGraphSummaries,
    elapsedSeconds,
    totalSamples,
    windowStartSeconds,
    windowEndSeconds,
    windowSamples,
    timestamp: new Date().toISOString(),
  };
}

function buildChannelSummaries(
  points: PL2.Point[],
  gC: ReturnType<typeof GCU.configWithId>,
  dvc: PL2.DataViewConfig,
  elapsedSeconds: number,
): ChannelSummary[] {
  const channelCount = (dvc.l ?? gC.labels ?? []).length;
  const summaries: ChannelSummary[] = [];

  for (let ch = 0; ch < channelCount; ch++) {
    const label = (dvc.l ?? gC.labels)?.[ch] ?? `Channel ${ch}`;
    const unit = UU.currentUnitsForConfig(gC, ch, dvc.uIdxs ?? []);

    const timeValues: Array<[number, number]> = [];
    for (const p of points) {
      const t = CU.timeFromPoint(p);
      const converted = UU.convertPoint(gC, p, dvc.uIdxs ?? []);
      const val = converted[ch + 1] as number;
      if (t != null && val != null && !isNaN(val)) {
        timeValues.push([t, val]);
      }
    }

    if (!timeValues.length) {
      summaries.push(emptyChannelSummary(label, unit));
      continue;
    }

    const values = timeValues.map(([, v]) => v);
    const first = values[0];
    const latest = values[values.length - 1];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance =
      values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
    const standardDeviation = Math.sqrt(variance);
    const deltaFromStart = latest - first;
    const range = max - min;
    const rateOfChange =
      elapsedSeconds > 0 ? deltaFromStart / elapsedSeconds : 0;

    let linearFit: ChannelSummary['linearFit'];
    let trend: TrendType = 'stable';

    if (timeValues.length >= MIN_POINTS_FOR_TREND) {
      const fit = linearRegression(timeValues);
      linearFit = fit;

      const significantThreshold = gC.significantChange?.[ch] ?? range * 0.1;
      const significantSlope =
        elapsedSeconds > 0 ? significantThreshold / elapsedSeconds : 0;

      if (fit.r2 < 0.3) {
        trend = 'variable';
      } else if (Math.abs(fit.slope) < significantSlope * 0.1) {
        trend = 'stable';
      } else if (fit.slope > 0) {
        trend = 'increasing';
      } else {
        trend = 'decreasing';
      }
    }

    summaries.push({
      label,
      unit,
      min,
      max,
      mean,
      standardDeviation,
      latest,
      first,
      deltaFromStart,
      range,
      trend,
      rateOfChange,
      linearFit,
    });
  }

  return summaries;
}

function linearRegression(points: Array<[number, number]>): {
  equation: string;
  r2: number;
  slope: number;
  intercept: number;
} {
  const n = points.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  let sumY2 = 0;

  for (const [x, y] of points) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
    sumY2 += y * y;
  }

  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) {
    return {equation: 'y = 0', r2: 0, slope: 0, intercept: sumY / n};
  }

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  const ssTot = sumY2 - (sumY * sumY) / n;
  const ssRes = points.reduce((sum, [x, y]) => {
    const predicted = slope * x + intercept;
    return sum + (y - predicted) ** 2;
  }, 0);

  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;

  return {
    equation: `y = ${slope.toFixed(4)}x + ${intercept.toFixed(2)}`,
    r2,
    slope,
    intercept,
  };
}

function mapDataStatus(
  status: PL2.DataStatus | undefined,
): 'live' | 'recording' | 'recorded' {
  switch (status) {
    case PL2.DataStatus.Record:
      return 'recording';
    case PL2.DataStatus.Recorded:
    case PL2.DataStatus.Play:
      return 'recorded';
    default:
      return 'live';
  }
}

function emptyChannelSummary(label: string, unit: string): ChannelSummary {
  return {
    label,
    unit,
    min: 0,
    max: 0,
    mean: 0,
    standardDeviation: 0,
    latest: 0,
    first: 0,
    deltaFromStart: 0,
    range: 0,
    trend: 'stable',
    rateOfChange: 0,
  };
}
